import { useMemo } from 'react';
import { Loader2 } from 'lucide-react';
import { useEnergyTimeSeries } from '@/hooks/useEnergyTimeSeries';
import type { CompareSlot, TimeGranularity, GroupTimeSeriesPoint } from '@/types/reports';
import type { Group, IndustryType } from '@/types';
import Chart from 'react-apexcharts';
import type { ApexOptions } from 'apexcharts';
import { energyColors } from '@/components/dashboard/charts/chartConfig';

interface CompareEnergyMixSectionProps {
  slot: CompareSlot;
  dateRange: { start: Date | null; end: Date | null } | null;
  granularity: TimeGranularity;
  groups: Group[];
  selectedIndustryTypes: IndustryType[];
}

export function CompareEnergyMixSection({
  slot,
  dateRange,
  granularity,
  groups: _groups,
  selectedIndustryTypes,
}: CompareEnergyMixSectionProps) {
  // Fetch time series data for this slot
  const { timeSeries: timeSeriesData, isLoading } = useEnergyTimeSeries({
    groupIds: slot.groupIds,
    startDate: dateRange?.start || null,
    endDate: dateRange?.end || null,
    granularity,
    modelCodes: slot.modelCodes,
    industryTypes: selectedIndustryTypes,
    enabled: slot.groupIds.length > 0 && !!dateRange,
  });

  // Calculate energy mix from time series data
  const energyMix = useMemo(() => {
    if (!timeSeriesData || timeSeriesData.length === 0) {
      return null;
    }

    const totals = timeSeriesData.reduce(
      (acc: { solar: number; grid: number; generator: number }, point: GroupTimeSeriesPoint) => ({
        solar: acc.solar + (point.solarKwh || 0),
        grid: acc.grid + (point.gridKwh || 0),
        generator: acc.generator + (point.generatorKwh || 0),
      }),
      { solar: 0, grid: 0, generator: 0 }
    );

    const total = totals.solar + totals.grid + totals.generator;
    if (total === 0) return null;

    return {
      solar: totals.solar,
      grid: totals.grid,
      generator: totals.generator,
      total,
      solarPercent: (totals.solar / total) * 100,
      gridPercent: (totals.grid / total) * 100,
      generatorPercent: (totals.generator / total) * 100,
    };
  }, [timeSeriesData]);

  const chartOptions: ApexOptions = useMemo(() => ({
    chart: {
      type: 'donut',
      background: 'transparent',
      animations: {
        enabled: true,
        speed: 300,
      },
    },
    colors: [energyColors.solar, energyColors.grid, energyColors.generator],
    labels: ['Solar', 'Grid', 'Generator'],
    legend: {
      show: true,
      position: 'bottom',
      labels: {
        colors: 'hsl(var(--foreground))',
      },
      markers: {
        offsetX: -4,
      },
    },
    dataLabels: {
      enabled: true,
      formatter: (val: number) => `${val.toFixed(0)}%`,
      style: {
        fontSize: '12px',
        fontWeight: 600,
      },
      dropShadow: { enabled: false },
    },
    plotOptions: {
      pie: {
        donut: {
          size: '65%',
          labels: {
            show: true,
            name: {
              show: true,
              fontSize: '12px',
              color: 'hsl(var(--muted-foreground))',
            },
            value: {
              show: true,
              fontSize: '18px',
              fontWeight: 600,
              color: 'hsl(var(--foreground))',
              formatter: (val) => `${Number(val).toLocaleString(undefined, { maximumFractionDigits: 0 })} kWh`,
            },
            total: {
              show: true,
              label: 'Total',
              fontSize: '12px',
              color: 'hsl(var(--muted-foreground))',
              formatter: (w) => {
                const total = w.globals.seriesTotals.reduce((a: number, b: number) => a + b, 0);
                return `${total.toLocaleString(undefined, { maximumFractionDigits: 0 })} kWh`;
              },
            },
          },
        },
      },
    },
    stroke: {
      show: false,
    },
    tooltip: {
      enabled: true,
      theme: 'dark',
      y: {
        formatter: (val) => `${val.toLocaleString(undefined, { maximumFractionDigits: 0 })} kWh`,
      },
    },
  }), []);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full min-h-[250px]">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!energyMix) {
    return (
      <div className="flex items-center justify-center h-full min-h-[250px] text-sm text-muted-foreground">
        No data available for selected period
      </div>
    );
  }

  return (
    <div className="h-[250px]">
      <Chart
        options={chartOptions}
        series={[energyMix.solar, energyMix.grid, energyMix.generator]}
        type="donut"
        height="100%"
      />
    </div>
  );
}
